/**
 * PATH: packages/email-hub/src/feature/components/ComposeAttachmentStrip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-COMPOSEATTACHMENTSTRIP-001
 * PURPOSE: Outlook-style cards for the LOCAL `File` attachments on a
 *          compose draft — file-type icon, name (with a "Signed" badge
 *          for countersigned PDFs), size, click-to-preview, and remove.
 * WHY:     The old strip was a plain paperclip + truncated name with no
 *          type icon and no way to open the file. Operator (2026-06-07):
 *          "why doesn't it show the PDF and structure like outlook when
 *          attaching pdfs … why can't I click and see the pdf in the
 *          frame". This renders a proper card and opens the blob in a
 *          preview modal on click. `inline` lets the host drop the cards
 *          into the same lane as the "Draft with AI" button.
 * FLOW:
 *   ┌──────────┐   ┌──────────────────┐   ┌─────────────────────────┐
 *   │ File[]   │ → │ card per file +  │ → │ click → preview modal   │
 *   │          │   │ type icon/badge  │   │ X → onRemove(index)     │
 *   └──────────┘   └──────────────────┘   └─────────────────────────┘
 * RELATED:
 *   - components/ComposeAttachmentPreviewModal.tsx: the blob previewer.
 *   - lib/signedAttachment.ts: the "(signed).pdf" detector for the badge.
 *   - pages/ComposePage.tsx: host that owns the `attachments` state.
 */

import { useState } from 'react'
import {
  File as FileIcon,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  ShieldCheck,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { isInitialledAttachmentName, isSignedAttachmentName } from '../lib/signedAttachment'
import ComposeAttachmentPreviewModal from './ComposeAttachmentPreviewModal'

interface FileMeta {
  Icon: LucideIcon
  tint: string
  iconClass: string
  label: string
}

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'])
const WORD_EXT = new Set(['doc', 'docx'])
const SHEET_EXT = new Set(['xls', 'xlsx', 'xlsm', 'csv'])
const ARCHIVE_EXT = new Set(['zip', 'rar', '7z', 'tar', 'gz'])

/** Pure: filename → icon + palette + label for the card. */
export function fileMeta(name: string): FileMeta {
  const parts = name.split('.')
  const ext = (parts.length > 1 ? parts.pop()! : '').toLowerCase()
  if (ext === 'pdf') return { Icon: FileType, tint: 'bg-rose-500/10', iconClass: 'text-rose-600 dark:text-rose-400', label: 'PDF' }
  if (IMAGE_EXT.has(ext)) return { Icon: FileImage, tint: 'bg-sky-500/10', iconClass: 'text-sky-600 dark:text-sky-400', label: 'Image' }
  if (WORD_EXT.has(ext)) return { Icon: FileText, tint: 'bg-blue-500/10', iconClass: 'text-blue-600 dark:text-blue-400', label: 'Word' }
  if (SHEET_EXT.has(ext)) return { Icon: FileSpreadsheet, tint: 'bg-emerald-500/10', iconClass: 'text-emerald-600 dark:text-emerald-400', label: 'Sheet' }
  if (ARCHIVE_EXT.has(ext)) return { Icon: FileArchive, tint: 'bg-amber-500/10', iconClass: 'text-amber-600 dark:text-amber-400', label: 'Archive' }
  return { Icon: FileIcon, tint: 'bg-slate-500/10', iconClass: 'text-slate-600 dark:text-slate-300', label: ext ? ext.toUpperCase() : 'File' }
}

/** Pure: bytes → "12 KB" / "1.4 MB". */
export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

interface AttachmentCardProps {
  file: File
  onOpen: () => void
  onRemove: () => void
}

function AttachmentCard({ file, onOpen, onRemove }: AttachmentCardProps) {
  const meta = fileMeta(file.name)
  const signed = isSignedAttachmentName(file.name)
  const initialled = !signed && isInitialledAttachmentName(file.name)
  const Icon = meta.Icon
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group relative flex items-center gap-3 w-[260px] max-w-full p-2 pr-8 rounded-lg border border-border bg-background hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      title={`${signed ? 'Signed · ' : initialled ? 'Initialled · ' : ''}${file.name} - ${formatSize(file.size)} · click to preview`}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${meta.tint}`}>
        <Icon className={`h-5 w-5 ${meta.iconClass}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate leading-tight flex items-center gap-1.5">
          {signed && (
            <span className="inline-flex items-center gap-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide shrink-0">
              <ShieldCheck className="h-2.5 w-2.5" /> Signed
            </span>
          )}
          {initialled && (
            <span className="inline-flex items-center gap-0.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide shrink-0">
              <ShieldCheck className="h-2.5 w-2.5" /> Initialled
            </span>
          )}
          <span className="truncate">{file.name}</span>
        </p>
        <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
          <span className={`font-medium ${meta.iconClass}`}>{meta.label}</span>
          {' · '}
          {formatSize(file.size)}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        className="absolute top-1.5 right-1.5 p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        aria-label={`Remove ${file.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export interface ComposeAttachmentStripProps {
  files: File[]
  onRemove: (index: number) => void
  /** When true, render the cards as bare flex items (no row wrapper /
   *  border) so the host can place them in the SAME lane as the
   *  "Draft with AI" button (operator 2026-06-07: "why is the
   *  attachment showing there not in the line with draft with AI"). */
  inline?: boolean
}

export default function ComposeAttachmentStrip({ files, onRemove, inline = false }: ComposeAttachmentStripProps) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  if (files.length === 0) return null

  const cards = files.map((file, i) => (
    <AttachmentCard
      key={`${file.name}-${i}`}
      file={file}
      onOpen={() => setPreviewIndex(i)}
      onRemove={() => onRemove(i)}
    />
  ))

  const modal = previewIndex !== null ? (
    <ComposeAttachmentPreviewModal
      files={files}
      initialIndex={previewIndex}
      onClose={() => setPreviewIndex(null)}
    />
  ) : null

  // Inline: emit the cards straight into the parent flex row.
  if (inline) {
    return (
      <>
        {cards}
        {modal}
      </>
    )
  }

  // Standalone (e.g. new-message compose with no AI lane): own bordered row.
  return (
    <div className="mb-3 flex flex-wrap gap-2 border-b border-border pb-3">
      {cards}
      {modal}
    </div>
  )
}
